import {
  FC,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTestFinish,
  useTestModel,
} from "@repo/edtech-api";
import { CrossSmallIcon } from "@repo/edtech-assets";
import { Button, Loader } from "@repo/edtech-ui-kit";
import clsx from "clsx";

import {
  BackTestQuestionType,
  useIdContext,
  useMainContextValues,
} from "../../../model";
import { SurveyQuestionItem } from "./components";

import st from "./CourseSurvey.module.scss";

interface Props {
  surveyId: string,
}

const ALLOWED_QUESTION_TYPES = [
  BackTestQuestionType.Closed,
  BackTestQuestionType.Open,
  BackTestQuestionType.Survey,
];

export const CourseSurvey: FC<Props> = ({ surveyId }) => {
  const { publicationId, unitId: mainUnitId } = useIdContext();
  const { currentLesson } = useMainContextValues();
  const unitId = currentLesson?.unitParentId ?? "";

  const [ answersState, setAnswersState ] = useState<Record<string, boolean>>({});
  const [ initializedCount, setInitializedCount ] = useState(0);
  const [ isFinished, setIsFinished ] = useState(false);
  const [ isHidden, setIsHidden ] = useState(false);
  const [ isSubmittingAnswers, setIsSubmittingAnswers ] = useState(false);
  const submitHandlersRef = useRef<Record<string, () => Promise<void>>>({});

  const {
    data: surveyData,
    isLoading: isSurveyLoading,
  } = useTestModel({
    publicationid: publicationId,
    unitid: unitId,
    testid: surveyId,
    enabled: !!surveyId && !!unitId,
  });

  const { trigger: finishSurvey, isMutating: isFinishing } = useTestFinish({
    publicationId,
    unitId,
    testId: surveyId,
    session: surveyData?.session,
    mainUnitId,
    survey: true,
  });

  const questions = useMemo(() => {
    return surveyData?.questions
      ?.filter((q) => ALLOWED_QUESTION_TYPES.includes(q.questionType as BackTestQuestionType))
      .map((q) => ({
        uid: q.uid,
        question: q.question,
        questionType: q.questionType as BackTestQuestionType,
      })) ?? [];
  }, [ surveyData?.questions ]);

  const handleAnswerChanged = useCallback((questionUid: string, isAnswered: boolean) => {
    setAnswersState((prev) => {
      if (prev[ questionUid ] === isAnswered) {
        return prev;
      }

      return {
        ...prev,
        [ questionUid ]: isAnswered,
      };
    });
  }, []);

  const handleRegisterSubmitHandler = useCallback((questionUid: string,
    submitHandler: (() => Promise<void>) | null) => {
    if (submitHandler) {
      submitHandlersRef.current[ questionUid ] = submitHandler;
      return;
    }

    delete submitHandlersRef.current[ questionUid ];
  }, []);

  const handleQuestionInitialized = useCallback(() => {
    setInitializedCount((prev) => prev + 1);
  }, []);

  const handleFinish = useCallback(async () => {
    if (isSubmittingAnswers || isFinishing) {
      return;
    }

    try {
      setIsSubmittingAnswers(true);

      for (const question of questions) {
        const submitQuestionAnswer = submitHandlersRef.current[ question.uid ];

        if (!submitQuestionAnswer) {
          continue;
        }

        await submitQuestionAnswer();
      }

      const result = await finishSurvey();
      if (result?.analyticsset) {
        setIsFinished(true);
      }
    } catch (error) {
      console.error("Error finishing survey:", error);
    } finally {
      setIsSubmittingAnswers(false);
    }
  }, [
    finishSurvey,
    isFinishing,
    isSubmittingAnswers,
    questions,
  ]);

  const handleClose = useCallback(() => {
    setIsHidden(true);
  }, []);

  const allAnswered = questions.length > 0 && questions.every((question) => answersState[ question.uid ]);
  const allInitialized = initializedCount >= questions.length && questions.length > 0;

  if (isHidden || isSurveyLoading || !surveyData || questions.length === 0) {
    return null;
  }

  if (isFinished) {
    return (
      <div className={st.thanks}>
        <button
          aria-label="Закрыть опрос"
          className={st.close_button}
          type="button"
          onClick={handleClose}
        >
          <CrossSmallIcon />
        </button>
        <div className={st.thanks_message}>
          <span className={st.thanks_title}>Спасибо за ответы!</span>
          <span className={st.thanks_subtitle}>Они помогут нам сделать курс лучше</span>
        </div>
      </div>
    );
  }

  return (
    <div className={st.container}>
      <h3 className={st.title}>{surveyData.title}</h3>

      {
        !allInitialized && (
          <div className={st.loader_wrapper}>
            <Loader />
          </div>
        )
      }

      <div className={clsx(st.questions, !allInitialized && st.hidden)}>
        {
          questions.map((q) => (
            <SurveyQuestionItem
              key={q.uid}
              publicationId={publicationId}
              questionText={q.question}
              questionType={q.questionType}
              questionUid={q.uid}
              session={surveyData.session}
              testId={surveyId}
              unitId={unitId}
              onAnswerChanged={handleAnswerChanged}
              onInitialized={handleQuestionInitialized}
              onRegisterSubmitHandler={handleRegisterSubmitHandler}
            />
          ))
        }

        {
          allInitialized && (
            <Button
              className={st.finish_button}
              disabled={!allAnswered || isFinishing || isSubmittingAnswers}
              onClick={handleFinish}
            >
              Завершить
            </Button>
          )
        }
      </div>
    </div>
  );
};
